import { useState, useEffect } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useUpdateCondominium } from "@/hooks/useCondominiums"
import { CondominiumWithSubscription } from "@/types/superadmin"
import { ORG_TYPE_PRESET, normalizeModuleSelection, resolveOrgModules, type AppModule, type OrgType } from "@/lib/modules"
import { ModulePicker } from "./ModulePicker"

interface EditCondominiumModalProps {
  isOpen: boolean
  onClose: () => void
  condominium: CondominiumWithSubscription | null
}

export const EditCondominiumModal = ({ isOpen, onClose, condominium }: EditCondominiumModalProps) => {
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    payment_status: "active",
    subscription_expires_at: "",
    type: "condominio" as OrgType,
    enabled_modules: ORG_TYPE_PRESET.condominio as AppModule[]
  })

  const updateMutation = useUpdateCondominium()

  useEffect(() => {
    if (condominium) {
      setFormData({
        name: condominium.name || "",
        address: condominium.address || "",
        payment_status: condominium.payment_status || "active",
        subscription_expires_at: condominium.subscription_expires_at 
          ? new Date(condominium.subscription_expires_at).toISOString().split('T')[0]
          : "",
        type: (condominium.type as OrgType) || "condominio",
        enabled_modules: resolveOrgModules(condominium)
      })
    }
  }, [condominium])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!condominium) return
    const current = normalizeModuleSelection(resolveOrgModules(condominium))
    const next = normalizeModuleSelection(formData.enabled_modules)
    const modulesChanged = current.length !== next.length || next.some((m) => !current.includes(m))

    try {
      await updateMutation.mutateAsync({
        id: condominium.id,
        name: formData.name,
        address: formData.address,
        payment_status: formData.payment_status,
        subscription_expires_at: formData.subscription_expires_at 
          ? new Date(formData.subscription_expires_at).toISOString()
          : null,
        type: formData.type,
        enabled_modules: modulesChanged ? next : undefined
      })
      onClose()
    } catch (error) {
      console.error("Erro ao atualizar organização:", error)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Editar Organização</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name">Nome</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div>
            <Label htmlFor="address">Endereço</Label>
            <Input
              id="address"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
            />
          </div>

          <div>
            <Label htmlFor="type">Tipo</Label>
            <Select value={formData.type} onValueChange={(value: OrgType) => 
              setFormData({ ...formData, type: value, enabled_modules: ORG_TYPE_PRESET[value] })
            }>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="condominio">Condomínio</SelectItem>
                <SelectItem value="ponto_eletronico">Ponto Eletrônico</SelectItem>
                <SelectItem value="controle_acessos">Controle de Acessos</SelectItem>
              </SelectContent>
            </Select>
            <p className="mt-1 text-[12px] text-muted-foreground">Trocar o tipo aplica o preset de módulos.</p>
          </div>

          <div>
            <Label>Módulos</Label>
            <div className="mt-1.5">
              <ModulePicker value={formData.enabled_modules} onChange={(enabled_modules) => setFormData({ ...formData, enabled_modules })} />
            </div>
          </div>

          <div>
            <Label htmlFor="payment_status">Status de Pagamento</Label>
            <Select value={formData.payment_status} onValueChange={(value) => 
              setFormData({ ...formData, payment_status: value })
            }>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Ativo</SelectItem>
                <SelectItem value="pending">Pendente</SelectItem>
                <SelectItem value="overdue">Em Atraso</SelectItem>
                <SelectItem value="suspended">Suspenso</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="expires_at">Data de Expiração</Label>
            <Input
              id="expires_at"
              type="date"
              value={formData.subscription_expires_at}
              onChange={(e) => setFormData({ ...formData, subscription_expires_at: e.target.value })}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending ? "Salvando..." : "Salvar"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
